import React, { useEffect, useState, useCallback, useMemo } from "react";
import Loader from "../../../Loader";
import { utils } from "../../../../utils/utils";
import {
  Button,
  Col,
  Row,
  Collapse,
  Table,
  CustomInput,
  Label,
  FormGroup,
} from "reactstrap";
import ConfirmationModal from "../../../ConfirmationModal";
import InformationModal from "../../../InformationModal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faChevronRight,
  faSearchPlus,
  faSearchMinus,
} from "@fortawesome/free-solid-svg-icons";
import { preProductionApi } from "../../../../services/preProductionServices";
import { useJobDetails } from "../../../../providers/jobDetailsProvider";

const DATE_FORMAT = "MM/DD/YY LT";

const PreProductionInfoTab = () => {
  const [sequences, setSequences] = useState([]);
  const [jobDetails] = useJobDetails();
  const [loading, setLoading] = useState();
  const [preProductionStatuses, setPreProductionStatuses] = useState([]);
  const [sequenceSelected, setSequenceSelected] = useState();
  const [informationModal, setInformationModal] = useState(
    utils.initInformationModal
  );
  const [itemDetails, setItemDetails] = useState({});
  const [itemChanges, setItemChanges] = useState({});
  const [confirmationModal, setConfirmationModal] = useState(
    utils.initConfirmationModal
  );

  // Memoize sequences with the injected "Detailing Status" item for display
  const sequencesForDisplay = useMemo(() => {
    return sequences.map((seq) => {
      if (!seq.preProductionItems || !seq.sequenceStatus) {
        return { ...seq, displayItems: seq.preProductionItems || [] };
      }

      const displayItems = [...seq.preProductionItems];
      // Insert Detailing Status as the second element (index 1)
      displayItems.splice(1, 0, {
        isSequenceStatus: true,
        id: `sequence-status-${seq.id}`,
        preProductionItemType: { name: "Detailing Status" },
        preProductionStatus: { name: seq.sequenceStatus.description },
      });

      return { ...seq, displayItems };
    });
  }, [sequences]);

  const selectSequence = (sequenceId) => {
    if (sequenceId === sequenceSelected) {
      sequenceId = null;
    }
    setSequenceSelected(sequenceId);
  };

  const onToggleItemDetails = (item) =>
    setItemDetails({
      ...itemDetails,
      [item.id]: !itemDetails[item.id],
    });

  const handleStatusChange = (itemId, newStatus) => {
    setItemChanges({
      ...itemChanges,
      [itemId]: {
        ...itemChanges[itemId],
        status: newStatus,
      },
    });
  };

  const handleNotesChange = (itemId, newNotes) => {
    setItemChanges({
      ...itemChanges,
      [itemId]: {
        ...itemChanges[itemId],
        notes: newNotes,
      },
    });
  };

  const saveItemChanges = async (itemId) => {
    try {
      setLoading(true);

      const changes = itemChanges[itemId];
      const updateData = {};

      // Add status change if exists
      if (changes.status !== undefined) {
        updateData.preProductionStatusId = parseInt(changes.status);
      }

      // Add notes change if exists
      if (changes.notes !== undefined) {
        updateData.notes = changes.notes;
      }

      await preProductionApi.updatePreProductionItem(itemId, updateData);

      // Clear changes after save
      const updatedChanges = { ...itemChanges };
      delete updatedChanges[itemId];
      setItemChanges(updatedChanges);

      // Clear expanded item details to force re-evaluation of disabled state
      setItemDetails({});

      // Refresh data and re-evaluate all validations
      await loadPreProductionItems(jobDetails.job.id);

      // Force re-render by briefly setting loading to false and back
      setLoading(false);
    } catch (error) {
      setLoading(false);
      setInformationModal({
        isOpen: true,
        title: "Error",
        body: "There was an error saving the changes.",
      });
    }
  };

  const hasChanges = (itemId) => {
    return (
      itemChanges[itemId] &&
      (itemChanges[itemId].status !== undefined ||
        itemChanges[itemId].notes !== undefined)
    );
  };

  const handleStatusButtonClick = (item) => {
    const currentStatus = item.preProductionStatus;
    const newStatus = getNextStatus(currentStatus);

    if (!newStatus) {
      return; // No next status available
    }

    setConfirmationModal({
      isOpen: true,
      title: "Confirm Status Change",
      body: `Are you sure you want to change the status of "${item.preProductionItemType?.name}" from "${currentStatus?.name}" to "${newStatus.name}"?`,
      onSubmit: async () => {
        setConfirmationModal(utils.initConfirmationModal);
        await confirmStatusChange(item, newStatus.id, newStatus.name);
      },
      onClose: () => setConfirmationModal(utils.initConfirmationModal),
    });
  };

  const confirmStatusChange = async (item, newStatusId, newStatusName) => {
    try {
      setLoading(true);

      await preProductionApi.updatePreProductionItem(item.id, {
        preProductionStatusId: newStatusId,
      });

      // Clear expanded item details to force re-evaluation of disabled state
      setItemDetails({});

      // Refresh data and re-evaluate all validations
      await loadPreProductionItems(jobDetails.job.id);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      setInformationModal({
        isOpen: true,
        title: "Error",
        body: "There was an error updating the status.",
      });
    }
  };

  const handleSaveChangesClick = (item) => {
    const changes = itemChanges[item.id];
    const changesDescription = [];

    if (changes.status !== undefined) {
      const newStatusName = preProductionStatuses.find(
        (s) => s.id === parseInt(changes.status)
      )?.name;
      changesDescription.push(
        `Status: "${item.preProductionStatus?.name}" → "${newStatusName}"`
      );
    }

    if (changes.notes !== undefined) {
      changesDescription.push("Notes");
    }

    setConfirmationModal({
      isOpen: true,
      title: "Confirm Changes",
      body: `Are you sure you want to save the changes for "${item.preProductionItemType?.name}"?`,
      onSubmit: async () => {
        setConfirmationModal(utils.initConfirmationModal);
        await saveItemChanges(item.id);
      },
      onClose: () => setConfirmationModal(utils.initConfirmationModal),
    });
  };

  const loadPreProductionItems = useCallback(async (jobId) => {
    try {
      setLoading(true);
      const response = await preProductionApi.getAllPreProductionItems({
        jobId,
      });
      setSequences(response.data);
      setLoading(false);
      return response.data;
    } catch (err) {
      setLoading(false);
      return setInformationModal({
        isOpen: true,
        title: "Error",
        body: "There was an error with your request.",
      });
    }
  }, []);

  const loadPreProductionStatuses = useCallback(async () => {
    try {
      const response = await preProductionApi.getPreProductionStatuses();
      setPreProductionStatuses(response.data || response);
    } catch (error) {
      console.error("Error loading pre-production statuses:", error);
    }
  }, []);

  // Helper function to get the next status
  const getNextStatus = useCallback(
    (currentStatus) => {
      if (!currentStatus || preProductionStatuses.length === 0) {
        return null;
      }

      const currentStatusIndex = preProductionStatuses.findIndex(
        (status) => status.id === currentStatus.id
      );

      if (
        currentStatusIndex !== -1 &&
        currentStatusIndex < preProductionStatuses.length - 1
      ) {
        return preProductionStatuses[currentStatusIndex + 1];
      }

      return null;
    },
    [preProductionStatuses]
  );

  // Helper function to get the next status name to display
  const getNextStatusLabel = useCallback(
    (currentStatus) => {
      if (!currentStatus) return "Set Status";

      // If preProductionStatuses is not loaded yet, show current status
      if (preProductionStatuses.length === 0) {
        return currentStatus.name;
      }

      const nextStatus = getNextStatus(currentStatus);

      if (nextStatus) {
        return `Set ${nextStatus.name}`; // Add "Set " prefix
      }

      // If no next status, this means it's the last status
      return currentStatus.name;
    },
    [getNextStatus, preProductionStatuses.length]
  );

  // Helper function to check if any default item is incomplete
  const hasIncompleteDefaultItem = useCallback((sequence) => {
    if (!sequence?.preProductionItems) return false;

    return sequence.preProductionItems.some(
      (item) =>
        item.preProductionItemType?.isDefault === true &&
        item.preProductionStatus?.name !== "Completed"
    );
  }, []);

  // Helper function to check if an item should be disabled
  const isItemDisabled = useCallback(
    (sequence, currentItem) => {
      if (!sequence?.preProductionItems) return false;

      // If current item is default, it's never disabled
      if (currentItem.preProductionItemType?.isDefault === true) {
        return false;
      }

      // If there are incomplete default items, disable this item
      return hasIncompleteDefaultItem(sequence);
    },
    [hasIncompleteDefaultItem]
  );

  // Load pre-production items and statuses
  useEffect(() => {
    loadPreProductionItems(jobDetails.job.id);
    loadPreProductionStatuses();
  }, [jobDetails.job.id, loadPreProductionItems, loadPreProductionStatuses]);

  return (
    <Col className="mt-3 col-12">
      <Row className="d-flex justify-content-between px-3 align-items-center">
        <h3 className="font-weight-bold">Pre-Production</h3>
      </Row>
      <Row className="d-flex justify-content-between px-3 mt-4 align-items-center">
        {loading ? (
          <Loader size="sm" />
        ) : informationModal.isOpen ? (
          <InformationModal
            title={informationModal.title}
            body={informationModal.body}
            onClose={() =>
              informationModal.onClose
                ? informationModal.onClose()
                : setInformationModal(utils.initInformationModal)
            }
          />
        ) : sequences.length ? (
          sequencesForDisplay.map((sequence, index) => (
            <div className="d-flex w-100 mt-2" key={`sequence-${sequence?.id}`}>
              <div className="ml-3 mr-0 flex-grow-1 overflow-x-auto">
                <div
                  onClick={() => {
                    selectSequence(sequence?.id);
                  }}
                  className="d-flex justify-content-between align-middle bg-primary border border-primary rounded text-white py-2 px-3 m-0 cursor-pointer"
                >
                  <span>{sequence.description}</span>
                  <div className="px-0 col-3 d-flex align-items-center justify-content-end">
                    <FontAwesomeIcon
                      icon={
                        sequenceSelected === sequence?.id
                          ? faChevronDown
                          : faChevronRight
                      }
                      fixedWidth
                      className="my-1"
                    />
                  </div>
                </div>
                <Collapse
                  isOpen={sequenceSelected === sequence?.id}
                  className="my-2 mb-0"
                >
                  <Row className="w-100 m-0">
                    <Table className="mt-2 border rounded">
                      <thead>
                        <tr className="bg-light border">
                          <th key="itemType">Pre-Production Steps</th>
                          <th key="status">Notes</th>
                          <th key="notes">Status</th>
                          <th key="lastUpdated" className="text-center">
                            Action
                          </th>
                        </tr>
                      </thead>
                      <tbody className={``}>
                        {sequence.displayItems?.length ? (
                          sequence.displayItems.map((item) => {
                            const disabled = item.isSequenceStatus
                              ? false
                              : isItemDisabled(sequence, item);
                            return (
                              <React.Fragment
                                key={`preproduction-item-${item.id}`}
                              >
                                <tr
                                  className={`border ${
                                    disabled ? "text-muted" : ""
                                  }`}
                                  style={disabled ? { opacity: 0.6 } : {}}
                                >
                                  <td>{item.preProductionItemType?.name}</td>
                                  <td
                                    className="text-truncate"
                                    title={item.notes || ""}
                                  >
                                    {item.isSequenceStatus
                                      ? ""
                                      : item.notes || "-"}
                                  </td>
                                  <td>
                                    {item.isSequenceStatus
                                      ? ""
                                      : item.preProductionStatus?.name}
                                  </td>
                                  <td>
                                    <div className="d-flex align-items-center justify-content-end">
                                      {item.isSequenceStatus ? (
                                        <span>
                                          {item.preProductionStatus?.name}
                                        </span>
                                      ) : !getNextStatus(
                                          item.preProductionStatus
                                        ) ? (
                                        <span
                                          className="btn btn-success btn-sm rounded mr-2 disabled"
                                          style={{ opacity: 1 }}
                                        >
                                          {item.preProductionStatus?.name}
                                        </span>
                                      ) : (
                                        <Button
                                          size="sm"
                                          className="rounded mr-2"
                                          color="primary"
                                          disabled={disabled}
                                          onClick={
                                            disabled
                                              ? undefined
                                              : () =>
                                                  handleStatusButtonClick(item)
                                          }
                                        >
                                          {getNextStatusLabel(
                                            item.preProductionStatus
                                          )}
                                        </Button>
                                      )}
                                      {!item.isSequenceStatus && (
                                        <Button
                                          size="sm"
                                          className="rounded"
                                          color="primary"
                                          disabled={disabled}
                                          onClick={
                                            disabled
                                              ? undefined
                                              : () => onToggleItemDetails(item)
                                          }
                                        >
                                          {itemDetails[item.id] ? (
                                            <FontAwesomeIcon
                                              icon={faSearchMinus}
                                            />
                                          ) : (
                                            <FontAwesomeIcon
                                              icon={faSearchPlus}
                                            />
                                          )}
                                        </Button>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                                {itemDetails[item.id] ? (
                                  <tr
                                    className={`border ${
                                      disabled ? "text-muted" : ""
                                    }`}
                                    style={disabled ? { opacity: 0.6 } : {}}
                                  >
                                    <td colSpan={4}>
                                      <div className="p-0">
                                        <Row>
                                          <Col md={6}>
                                            <FormGroup>
                                              <Label for={`status-${item.id}`}>
                                                Status
                                              </Label>
                                              <CustomInput
                                                type="select"
                                                id={`status-${item.id}`}
                                                value={
                                                  itemChanges[item.id]
                                                    ?.status ||
                                                  item.preProductionStatus
                                                    ?.id ||
                                                  ""
                                                }
                                                onChange={(e) =>
                                                  handleStatusChange(
                                                    item.id,
                                                    e.target.value
                                                  )
                                                }
                                                disabled={disabled}
                                              >
                                                <option value="" disabled>
                                                  Select a status...
                                                </option>
                                                {preProductionStatuses.map(
                                                  (status) => (
                                                    <option
                                                      key={status.id}
                                                      value={status.id}
                                                    >
                                                      {status.name}
                                                    </option>
                                                  )
                                                )}
                                              </CustomInput>
                                            </FormGroup>
                                            <FormGroup className="mb-0">
                                              <Label for={`notes-${item.id}`}>
                                                Notes
                                              </Label>
                                              <textarea
                                                id={`notes-${item.id}`}
                                                className="form-control bg-white"
                                                rows="4"
                                                value={
                                                  itemChanges[item.id]
                                                    ?.notes !== undefined
                                                    ? itemChanges[item.id]
                                                        ?.notes
                                                    : item.notes || ""
                                                }
                                                placeholder="Notes..."
                                                onChange={(e) =>
                                                  handleNotesChange(
                                                    item.id,
                                                    e.target.value
                                                  )
                                                }
                                                disabled={disabled}
                                              />
                                            </FormGroup>
                                            {hasChanges(item.id) &&
                                              !disabled && (
                                                <div className="mt-3">
                                                  <Button
                                                    color="success"
                                                    size="sm"
                                                    className="rounded"
                                                    onClick={() =>
                                                      handleSaveChangesClick(
                                                        item
                                                      )
                                                    }
                                                    disabled={disabled}
                                                  >
                                                    Save
                                                  </Button>
                                                </div>
                                              )}
                                          </Col>
                                          <Col md={6}>
                                            <h6 className="mb-2 pt-1">
                                              History
                                            </h6>
                                            <Table
                                              size="sm"
                                              className="border rounded"
                                            >
                                              <thead>
                                                <tr className="bg-light border">
                                                  <th
                                                    key="dateTime"
                                                    className="pl-2"
                                                  >
                                                    Date / Time
                                                  </th>
                                                  <th key="action">Action</th>
                                                  <th
                                                    key="actionBy"
                                                    className="pr-2"
                                                  >
                                                    Action By
                                                  </th>
                                                </tr>
                                              </thead>
                                              <tbody>
                                                {item.history?.length ? (
                                                  item.history.map(
                                                    (historyItem, i) => (
                                                      <tr
                                                        key={i}
                                                        className="border px-2"
                                                      >
                                                        <td className="pl-2">
                                                          {historyItem.createdAt
                                                            ? utils.formatDate(
                                                                historyItem.createdAt,
                                                                DATE_FORMAT
                                                              )
                                                            : "-"}
                                                        </td>
                                                        <td>
                                                          {historyItem.details ||
                                                            "-"}
                                                        </td>
                                                        <td className="pr-2">
                                                          {historyItem.user
                                                            ? `${historyItem.user.firstName} ${historyItem.user.lastName}`
                                                            : "-"}
                                                        </td>
                                                      </tr>
                                                    )
                                                  )
                                                ) : (
                                                  <tr className="border">
                                                    <td
                                                      colSpan={3}
                                                      className="text-muted small text-center"
                                                    >
                                                      No history available
                                                    </td>
                                                  </tr>
                                                )}
                                              </tbody>
                                            </Table>
                                          </Col>
                                        </Row>
                                      </div>
                                    </td>
                                  </tr>
                                ) : null}
                              </React.Fragment>
                            );
                          })
                        ) : (
                          <tr className="border">
                            <td
                              className="text-muted small text-center border"
                              colSpan={4}
                            >
                              No pre-production steps to show
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </Row>
                </Collapse>
              </div>
            </div>
          ))
        ) : (
          <div className="col-12 text-center small">No data to show</div>
        )}
      </Row>
      {confirmationModal.isOpen ? (
        <ConfirmationModal {...confirmationModal} />
      ) : null}
    </Col>
  );
};

export default PreProductionInfoTab;
